// usePresenceAndUnread.js

import { useEffect, useRef } from "react";
import { loadGroupKey, decryptWithGroupKey } from "../utils/Cryptoutils";

export function usePresenceAndUnread({
  socket,
  users,
  setUsers,
  setGroups,
  setTypingUsers,
  setGroupTyping,
  plaintextPreviewRef,
  cryptoFns,
}) {
  const previewDecryptDoneRef = useRef(false);

  /* =====================================================
     🟢 USER PRESENCE
  ===================================================== */
  useEffect(() => {
    if (!socket) return;

    const onOnline = ({ chatId }) =>
      setUsers((prev) =>
        prev.map((u) =>
          String(u.chatId) === String(chatId)
            ? { ...u, online: true }
            : u
        )
      );

    const onOffline = ({ chatId, lastSeenAt }) =>
      setUsers((prev) =>
        prev.map((u) =>
          String(u.chatId) === String(chatId)
            ? {
                ...u,
                online: false,
                lastSeenAt: lastSeenAt || u.lastSeenAt || null,
              }
            : u
        )
      );

    socket.on("user-online", onOnline);
    socket.on("user-offline", onOffline);

    return () => {
      socket.off("user-online", onOnline);
      socket.off("user-offline", onOffline);
    };
  }, [socket]);

  /* =====================================================
     💬 DM TYPING INDICATORS
  ===================================================== */
  useEffect(() => {
    if (!socket || !setTypingUsers) return;

    const onTyping = ({ senderChatId }) =>
      setTypingUsers((prev) =>
        prev.includes(String(senderChatId))
          ? prev
          : [...prev, String(senderChatId)]
      );

    const onStopTyping = ({ senderChatId }) =>
      setTypingUsers((prev) =>
        prev.filter((id) => id !== String(senderChatId))
      );

    socket.on("typing", onTyping);
    socket.on("stop-typing", onStopTyping);

    return () => {
      socket.off("typing", onTyping);
      socket.off("stop-typing", onStopTyping);
    };
  }, [socket, setTypingUsers]);

  /* =====================================================
     💬 GROUP TYPING INDICATORS
  ===================================================== */
  useEffect(() => {
    if (!socket || !setGroupTyping) return;

    const onGroupTyping = ({ groupId, chatId }) =>
      setGroupTyping((prev) => {
        const existing = prev[groupId] || [];
        if (existing.includes(String(chatId))) return prev;

        return {
          ...prev,
          [groupId]: [...existing, String(chatId)],
        };
      });

    const onGroupStopTyping = ({ groupId, chatId }) =>
      setGroupTyping((prev) => {
        const existing = prev[groupId] || [];
        if (!existing.includes(String(chatId))) return prev;

        return {
          ...prev,
          [groupId]: existing.filter((id) => id !== String(chatId)),
        };
      });

    socket.on("group-user-typing", onGroupTyping);
    socket.on("group-user-stop-typing", onGroupStopTyping);

    return () => {
      socket.off("group-user-typing", onGroupTyping);
      socket.off("group-user-stop-typing", onGroupStopTyping);
    };
  }, [socket, setGroupTyping]);

  /* =====================================================
     🔴 DM UNREAD COUNT
  ===================================================== */
  useEffect(() => {
    if (!socket) return;

    const onUnread = ({ fromChatId, unreadCount, timestamp }) =>
      setUsers((prev) =>
        prev.map((u) =>
          String(u.chatId) === String(fromChatId)
            ? {
                ...u,
                unreadCount,
                lastActivityAt: timestamp,
              }
            : u
        )
      );

    const onReset = ({ fromChatId, unreadCount }) =>
      setUsers((prev) =>
        prev.map((u) =>
          String(u.chatId) === String(fromChatId)
            ? { ...u, unreadCount }
            : u
        )
      );

    socket.on("unread-count", onUnread);
    socket.on("unread-reset", onReset);

    return () => {
      socket.off("unread-count", onUnread);
      socket.off("unread-reset", onReset);
    };
  }, [socket]);

  /* =====================================================
     🔴 GROUP UNREAD COUNT
  ===================================================== */
  useEffect(() => {
    if (!socket) return;

    const onUnread = ({ groupId, unreadCount, timestamp }) =>
      setGroups((prev) =>
        prev.map((g) =>
          String(g.groupId) === String(groupId)
            ? {
                ...g,
                unreadCount,
                lastActivityAt: timestamp,
              }
            : g
        )
      );

    const onReset = ({ groupId, unreadCount }) =>
      setGroups((prev) =>
        prev.map((g) =>
          String(g.groupId) === String(groupId)
            ? { ...g, unreadCount }
            : g
        )
      );

    socket.on("group-unread-count", onUnread);
    socket.on("group-unread-reset", onReset);

    return () => {
      socket.off("group-unread-count", onUnread);
      socket.off("group-unread-reset", onReset);
    };
  }, [socket]);

  return { previewDecryptDoneRef };
}